// Broadcast event types whose toast should only be shown for games the
// recipient actually plays. SESSION_CREATED and POST_CREATED are delivered to
// every online user (BROADCAST_ALL on the backend), so without this filter a
// player would be toasted about new sessions / LFG posts in games that aren't
// in their profile.
//
// Only the toast is filtered — query invalidation still runs for every event so
// the Discover feed stays complete across all games.
export const GAME_FILTERED_TOAST_EVENT_TYPES = new Set<string>([
  'SESSION_CREATED',
  'POST_CREATED',
]);

/**
 * Returns true when a toast should be suppressed because the session's / post's
 * game is not one the recipient plays.
 *
 * Shows the toast when the event isn't game-filtered, the message carries no
 * gameId, or the recipient's games are unknown (`null` — profile not loaded
 * yet, so we don't suppress on missing data). Once the games are known, only a
 * recipient whose list includes this game is toasted — an empty list (no games
 * picked) means no game matches, so the toast is suppressed.
 */
export const shouldSuppressToastByGame = (
  type: string,
  gameId: string | null | undefined,
  userGameIds: string[] | null | undefined
): boolean => {
  if (!GAME_FILTERED_TOAST_EVENT_TYPES.has(type)) return false;
  if (!gameId) return false;
  if (userGameIds == null) return false; // games unknown (not loaded) → show
  return !userGameIds.includes(gameId);
};

/**
 * Типы событий, чей тост глушится открытой на экране перепиской.
 *
 * Сообщение в переписке сессии приезжает СВОИМ типом (отдельный сабджект в шине), и в этом
 * списке его не было: человек читал ленту сессии и получал тост о каждом сообщении, которое
 * прямо у него на глазах и появлялось.
 */
const CHAT_TOAST_EVENT_TYPES = new Set<string>(['CHAT_MESSAGE', 'SESSION_CHAT_MESSAGE']);

/**
 * Returns true when a chat-message toast (and its coupled sound) should be
 * suppressed because the user is already looking at it, isn't looking at the
 * page at all, or has muted this conversation:
 *  - the message belongs to a chat open on screen (`chatOpen` – считает вызывающий по
 *    `useActiveChatStore.isChatOpen(parentId)`; открытых лент бывает несколько: окно комнаты
 *    сессии рисует переписку поверх любой страницы), or
 *  - the browser tab is hidden/backgrounded, or
 *  - the user muted this chat (`muted`, per-chat client-side setting).
 *
 * Applies only to chat messages (личные и в переписке сессии) – other notification types are
 * unaffected. The unread badge + query invalidation still run regardless (handled elsewhere), so
 * the user still sees the unread count; we only skip the noisy toast/sound.
 */
export const shouldSuppressChatToast = ({
  type,
  chatOpen,
  tabHidden,
  muted = false,
}: {
  type: string;
  chatOpen: boolean;
  tabHidden: boolean;
  muted?: boolean;
}): boolean => {
  if (!CHAT_TOAST_EVENT_TYPES.has(type)) return false;
  if (tabHidden) return true;
  if (muted) return true;
  return chatOpen;
};
